/**
 * Collaborators tab — lists the co-agents collaborating on this conversation
 * (KC2 "Shared" tab). Only rendered when collaborators exist. The assigned
 * agent can remove a collaborator here.
 */
import { useMemo, type JSX } from 'react'
import { UserCog, X } from 'lucide-react'
import { useAgentUsers } from '../../../hooks/useAgentUsers.js'
import { R } from './primitives.js'

interface CollaboratorsTabProps {
  coAgentIds: string[]
  /** Only the assigned agent may remove collaborators. */
  canEdit: boolean
  onRemove: (agentId: string) => Promise<boolean>
  busy: boolean
}

export default function CollaboratorsTab({
  coAgentIds,
  canEdit,
  onRemove,
  busy,
}: CollaboratorsTabProps): JSX.Element {
  const { users } = useAgentUsers(true)

  const labelFor = useMemo(() => {
    const map = new Map<string, string>()
    for (const u of users) if (u.agentId) map.set(u.agentId, u.loginid || u.name || 'Agent')
    return map
  }, [users])

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-5">
      <div className="mb-5">
        <h3 className="text-lg font-semibold text-slate-800">Collaborators</h3>
        <p className="text-xs text-slate-400">Agents collaborating on this conversation — they can reply, but not edit properties or transfer</p>
      </div>

      <ul className="space-y-2">
        {coAgentIds.map((id) => {
          const label = labelFor.get(id) ?? id
          return (
            <li
              key={id}
              className={`flex items-center justify-between ${R} border border-slate-200 bg-white px-4 py-3`}
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">
                  <UserCog className="h-4 w-4" />
                </span>
                <span className="truncate text-sm font-medium text-slate-800">{label}</span>
              </span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => void onRemove(id)}
                  disabled={busy}
                  title="Remove collaborator"
                  className={`flex h-8 w-8 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50`}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
